/**
 * Settings → Organisation (DEV-138): who Norvix is, stored once and read
 * everywhere it is printed. One definition for both sides: the back office
 * checks a form with it and shows what a save changes, and the API checks the
 * same rules before storing and builds the History from the same change list.
 *
 * The regulatory wording may name the legal entity through placeholders, so
 * the name and address are typed once: {legalName}, {address} and
 * {registrationNumber} are replaced when a document is produced.
 */

import type { SettingChange } from '../settings-diff/index.js';

export interface OrganisationSettings {
  /** The legal entity that operates Norvix, as registered. */
  readonly legalName: string;
  readonly registrationNumber: string;
  /** The registered address, one line per entry, printed joined by ", ". */
  readonly addressLines: readonly string[];
  /** The paragraphs under "Operator and regulatory information" on statements. */
  readonly statementDisclosures: readonly string[];
  /** ISO 4217. */
  readonly baseCurrency: string;
  /** ISO 4217; what reports are totalled in. */
  readonly reportingCurrency: string;
  /** IANA time zone, e.g. "UTC" or "Europe/Vilnius". */
  readonly timeZone: string;
  /** Daily payment cut-off, HH:MM in `timeZone`; empty when none is set. */
  readonly paymentCutoff: string;
  /** Where customers reach support; printed in security notices. */
  readonly supportEmail: string;
}

export type OrganisationField = keyof OrganisationSettings;

/** What people call each field, in form order. */
export const ORGANISATION_FIELD_LABELS: Readonly<Record<OrganisationField, string>> = {
  legalName: 'Legal name',
  registrationNumber: 'Registration number',
  addressLines: 'Registered address',
  statementDisclosures: 'Statement disclosure',
  baseCurrency: 'Base currency',
  reportingCurrency: 'Reporting currency',
  timeZone: 'Time zone',
  paymentCutoff: 'Payment cut-off',
  supportEmail: 'Support email',
};

export const ORGANISATION_FIELDS = Object.keys(ORGANISATION_FIELD_LABELS) as readonly OrganisationField[];

export const DISCLOSURE_PLACEHOLDERS = ['{legalName}', '{address}', '{registrationNumber}'] as const;

/** The most the statement footer holds: paragraphs, and characters once placeholders are filled in. */
export const MAX_DISCLOSURE_PARAGRAPHS = 3;
export const MAX_DISCLOSURE_CHARS = 600;
export const MAX_ADDRESS_LINES = 4;

const TEXT_MAX = 200;
/**
 * The longest legal name: the commission transfer sends it, in capitals, as
 * the recipient name, and the bank instruction carries at most 70 characters
 * (commission-transfer/request-body.ts). Refused here, when it is saved, not
 * on the night of the transfer.
 */
export const MAX_LEGAL_NAME_CHARS = 70;
const CURRENCY = /^[A-Z]{3}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
// Deliberately plain: one @, no spaces, a dot in the domain.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Any C0/C1 control character except the ones checked separately.
const CONTROL = /\p{Cc}/u;

/** The registered address on one line. */
export function organisationAddress(org: Pick<OrganisationSettings, 'addressLines'>): string {
  return org.addressLines.map((line) => line.trim()).filter((line) => line !== '').join(', ');
}

/** The statement paragraphs with the placeholders filled in, exactly as printed. */
export function statementDisclosures(org: OrganisationSettings): string[] {
  const values: Readonly<Record<string, string>> = {
    '{legalName}': org.legalName.trim(),
    '{address}': organisationAddress(org),
    '{registrationNumber}': org.registrationNumber.trim(),
  };
  return org.statementDisclosures.map((paragraph) =>
    paragraph.replace(/\{(legalName|address|registrationNumber)\}/g, (token) => values[token] ?? token).trim());
}

/** The two-line footer of a payment confirmation: brand and legal entity, then the address. */
export function confirmationFooter(org: OrganisationSettings): string {
  return `Norvix | ${org.legalName.trim()}\n${organisationAddress(org)}`;
}

/**
 * The name our own accounts are held under at the bank, which Verification of
 * Payee compares on the commission transfer: the legal name in capitals, as
 * GuruPay prints it.
 */
export function operatorAccountHolderName(org: Pick<OrganisationSettings, 'legalName'>): string {
  return org.legalName.trim().toUpperCase();
}

function validTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const lines = (value: unknown): string[] =>
  (Array.isArray(value) ? value : []).map(str).filter((line) => line !== '');

export type OrganisationValidation =
  | { readonly ok: true; readonly value: OrganisationSettings }
  | { readonly ok: false; readonly errors: Readonly<Partial<Record<OrganisationField, string>>> };

/** Trim and check a submitted Organisation. Never throws; errors are per field, for the form. */
export function validateOrganisation(input: unknown): OrganisationValidation {
  const raw = typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : {};
  const value: OrganisationSettings = {
    legalName: str(raw['legalName']),
    registrationNumber: str(raw['registrationNumber']),
    addressLines: lines(raw['addressLines']),
    statementDisclosures: lines(raw['statementDisclosures']),
    baseCurrency: str(raw['baseCurrency']).toUpperCase(),
    reportingCurrency: str(raw['reportingCurrency']).toUpperCase(),
    timeZone: str(raw['timeZone']),
    paymentCutoff: str(raw['paymentCutoff']),
    supportEmail: str(raw['supportEmail']).toLowerCase(),
  };
  const errors: Partial<Record<OrganisationField, string>> = {};
  const text = (field: 'legalName' | 'registrationNumber', label: string, max: number): void => {
    const v = value[field];
    // Counted in characters as the bank counts them, after upper-casing (ß becomes SS).
    const length = Math.max([...v].length, [...v.toUpperCase()].length);
    if (v === '') errors[field] = `Enter the ${label}.`;
    else if (length > max || CONTROL.test(v)) errors[field] = `The ${label} must be one line of at most ${String(max)} characters.`;
  };
  text('legalName', 'legal name', MAX_LEGAL_NAME_CHARS);
  text('registrationNumber', 'registration number', TEXT_MAX);
  if (value.addressLines.length === 0) errors.addressLines = 'Enter the registered address.';
  else if (value.addressLines.length > MAX_ADDRESS_LINES || value.addressLines.some((l) => l.length > TEXT_MAX || CONTROL.test(l))) {
    errors.addressLines = `Use at most ${String(MAX_ADDRESS_LINES)} lines of at most ${String(TEXT_MAX)} characters.`;
  }
  const unknownPlaceholder = value.statementDisclosures
    .flatMap((p) => p.match(/\{[^}]*\}/g) ?? [])
    .find((token) => !(DISCLOSURE_PLACEHOLDERS as readonly string[]).includes(token));
  if (value.statementDisclosures.length === 0) errors.statementDisclosures = 'Enter the statement disclosure.';
  else if (value.statementDisclosures.length > MAX_DISCLOSURE_PARAGRAPHS) {
    errors.statementDisclosures = `Use at most ${String(MAX_DISCLOSURE_PARAGRAPHS)} paragraphs.`;
  } else if (value.statementDisclosures.some((p) => CONTROL.test(p))) {
    errors.statementDisclosures = 'Each paragraph must be one line of text.';
  } else if (unknownPlaceholder !== undefined) {
    errors.statementDisclosures = `${unknownPlaceholder} is not a placeholder. Use ${DISCLOSURE_PLACEHOLDERS.join(', ')}.`;
  } else if (statementDisclosures(value).join('').length > MAX_DISCLOSURE_CHARS) {
    errors.statementDisclosures = `The disclosure must fit the statement footer: at most ${String(MAX_DISCLOSURE_CHARS)} characters with the placeholders filled in.`;
  }
  for (const field of ['baseCurrency', 'reportingCurrency'] as const) {
    if (!CURRENCY.test(value[field])) errors[field] = 'Use a three-letter ISO 4217 code, e.g. EUR.';
  }
  if (value.timeZone === '' || !validTimeZone(value.timeZone)) errors.timeZone = 'Use an IANA time zone, e.g. UTC or Europe/Vilnius.';
  if (value.paymentCutoff !== '' && !TIME.test(value.paymentCutoff)) errors.paymentCutoff = 'Use HH:MM (24-hour), or leave it empty.';
  if (!EMAIL.test(value.supportEmail) || value.supportEmail.length > 254) errors.supportEmail = 'Enter an email address.';
  return Object.keys(errors).length === 0 ? { ok: true, value } : { ok: false, errors };
}

/** A field's value as the review and the History show it. */
export function describeOrganisationValue(field: OrganisationField, org: OrganisationSettings): string {
  const value = org[field];
  // Lines and paragraphs stay apart, so re-splitting them is a visible change.
  if (field === 'addressLines') return org.addressLines.join(' / ');
  if (Array.isArray(value)) return value.map((p, i) => `¶${String(i + 1)} ${p}`).join(' ');
  return value === '' ? '—' : String(value);
}

/** Every field a save changes, in form order. `before` null: nothing was stored yet. */
export function organisationChanges(before: OrganisationSettings | null, after: OrganisationSettings): SettingChange[] {
  return ORGANISATION_FIELDS
    .map((field): SettingChange => ({
      key: field,
      label: ORGANISATION_FIELD_LABELS[field],
      before: before === null ? null : describeOrganisationValue(field, before),
      after: describeOrganisationValue(field, after),
    }))
    .filter((change) => change.before !== change.after);
}

export { organisationSchema } from './schema.js';
